/**
 * @file products.api.js
 * @module ProductsAPI
 * @description HTTP API wrapper for products and experiences endpoints. Methods return Axios promises.
 */

import { axiosInstance } from "./axios"
export const ProductsAPI = {

    /**
     * Retrieve products with pagination and optional query.
     * @param {number} limit
     * @param {number} page
     * @param {string} [query=""]
     * @param {string} token
     * @returns {Promise} Axios promise resolving to products list.
     */
    getProducts: async (limit, page, query = "", token = "") => {
        return await axiosInstance(token).get(`/products?limit=${limit}&page=${page}${query ? `&${query}` : ""}`);
    },

    /**
     * Get a product by id.
     * @param {string} productId
     * @param {string} [token=""]
     * @returns {Promise} Axios promise resolving to product data.
     */
    getProductById: async (productId, token = "") => {
        return await axiosInstance(token).get(`/products/${productId}`);
    },

    /**
     * Create a new product.
     * @param {Object} product
     * @param {string} [token=""]
     * @returns {Promise} Axios promise resolving to created product.
     */
    createProduct: async (product, token = "") => {
        return await axiosInstance(token).post(`/products`, product);
    },

    /**
     * Update a product by id.
     * @param {string} productId
     * @param {Object} product
     * @param {string} [token=""]
     * @returns {Promise} Axios promise resolving to updated product.
     */
    updateProductById: async (productId, product, token = "") => {
        return await axiosInstance(token).put(`/products/${productId}`, product);
    },

    /**
     * Delete a product by id.
     * @param {string} productId
     * @param {string} [token=""]
     * @returns {Promise} Axios promise resolving to deletion result.
     */
    deleteProductById: async (productId, token = "") => {
        return await axiosInstance(token).delete(`/products/${productId}`);
    },

    /**
     * Delete many products by payload.
     * @param {Array|Object} products
     * @param {string} [token=""]
     * @returns {Promise} Axios promise resolving to deletion result.
     */
    deleteManyProductsById: async (products, token = "") => {
        return await axiosInstance(token).put(`/bulk-delete-products`, { products: products });
    },

    /**
     * Activate or deactivate a product by id.
     * @param {string} productId
     * @param {boolean} status - The target status.
     * @param {string} [token=""]
     * @returns {Promise} Axios promise resolving to `{ updated, active }`.
     */
    updateStatusById: async (productId, status, token = "") => {
        return await axiosInstance(token).patch(`/products/${productId}/status`, { status });
    },

    /**
     * Get product prices using a quote payload.
     * @param {string} quote
     * @param {Object} payload
     * @param {string} [token=""]
     * @returns {Promise} Axios promise resolving to price data.
     */
    getProductPrices: async (quote, payload, token = "") => {
        return await axiosInstance(token).post(`/product-prices?quote=${quote}`, payload);
    },

    /**
     * Availability check for a product.
     * @param {string} productId
     * @param {string} date
     * @param {string} optionType
     * @param {string} [token=""]
     * @returns {Promise} Axios promise resolving to availability result.
     */
    availabilityCheck: (productId, date, optionType, token = "") => {
        return axiosInstance(token).post(`/products-availability`, { producId: productId, date: date, optionType: optionType });
    },

    /**
     * Get product prices by destination id.
     * @param {string} destinationId
     * @param {string} [token=""]
     * @returns {Promise} Axios promise resolving to product prices for the destination.
     */
    getProductPricesByDestinationsId: (destinationId, token = "") => {
        return axiosInstance(token).get(`/destinations/${destinationId}/product-prices`);
    },

    /**
     * Check if product slug is duplicate.
     * @param {string} productSlug
     * @param {string} [token=""]
     * @returns {Promise} Axios promise resolving to duplicate check result.
     */
    checkProductSlugIsDuplicate: (productSlug, token = "") => {
        return axiosInstance(token).get(`/product/${productSlug}/isDuplicate`);
    },

    /**
     * History service: get histories for a product.
     * @param {string} experienceId
     * @param {number} limit
     * @param {number} page
     * @param {string} [token=""]
     * @returns {Promise} Axios promise resolving to histories list.
     */
    getHistoriesByProductId: (experienceId, limit, page, token = "") => {
        return axiosInstance(token).get(`/modules/products/${experienceId}/histories?limit=${limit}&page=${page}`)
    },

    /**
     * Update a history entry by id.
     * @param {string} historyId
     * @param {Object} history
     * @param {string} [token=""]
     * @returns {Promise} Axios promise resolving to updated history.
     */
    updateHistoryById: (historyId, history, token = "") => {
        return axiosInstance(token).put(`/histories/${historyId}`, history)
    },

    /**
     * Bulk update commissions.
     * @param {FormData|Object} formData
     * @param {string} [token=""]
     * @returns {Promise} Axios promise resolving to update result.
     */
    bulkUpdateCommissions: (formData, token = "") => {
        return axiosInstance(token).post(`/bulk-update-commissions`, formData);
    },
    /**
     * Export products.
     * @param {Object} payload - Export options and filters.
     * @param {string} [token=""]
     * @returns {Promise} Axios promise resolving to export result.
     */
    exportProducts: (query, payload, token = "") => {
        return axiosInstance(token).post(`/export/products${query ? `?${query}` : ""}`, payload);
    }
}
